import { ChangeEvent, FC, MouseEvent, useMemo } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useServicesQuery } from 'TenantModule/api/queries/useServicesQuery';
import { OverrideItem } from 'TenantModule/components/Management/IntegrationAgentsNameSelector';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { SimpleMenuItem } from 'types';
import { filterItemWithKeywords, ItemFilter } from 'FiltersModule/utils';

type ServiceMenuItem = SimpleMenuItem & { value: string; name: string };

// Match typed keywords against both the canonical service name (value) and its
// human-readable label.
const itemFilter: ItemFilter = ({ searchWords, item }) =>
  filterItemWithKeywords({
    searchWords,
    item,
    filterKeysArray: ['value', 'name'],
  });

/**
 * Dropdown listing every service registered in galaxy-server (not just
 * integration agents). Submits the canonical `serviceName`, which
 * galaxy-server expects when registering a service to a tenant/realm.
 *
 * Opens a searchable popover (same mechanism as the realm dropdown) so a long
 * service list can be filtered by typing; the read-only field just shows the
 * current selection and anchors the menu.
 *
 * Pass `itemsOverride` to restrict the list to a caller-supplied set (e.g. the
 * Create Draft modal, which only offers draft-capable services); the full
 * service catalog query is skipped when an override is present.
 */
export const ServiceNameSelector: FC<{
  value: string;
  onChange: (event: ChangeEvent<any>) => void;
  disabled?: boolean;
  name?: string;
  itemsOverride?: OverrideItem[];
  helperText?: string;
}> = ({ value, onChange, disabled, name = 'integrationAgentName', itemsOverride, helperText }) => {
  const { services } = useServicesQuery({ enabled: !itemsOverride });
  const { openModal } = useDynamicModal();

  const items: ServiceMenuItem[] = useMemo(() => {
    const source: OverrideItem[] =
      itemsOverride ??
      services.map((service) => ({
        value: service.serviceName,
        name: service.serviceDisplayName || service.serviceName,
      }));
    return source.map((item) => ({
      label: item.name,
      value: item.value,
      name: item.name,
      disableButton: item.disabled,
    }));
  }, [itemsOverride, services]);

  // Show the selected service's display name; fall back to the raw value if the
  // catalog hasn't loaded a matching entry yet.
  const selectedLabel = items.find((item) => item.value === value)?.name ?? value;

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    const target = event.currentTarget;
    openModal({
      modal: ({ toggle }) => (
        <SimpleMenu<ServiceMenuItem>
          anchorEl={target}
          items={items}
          handleClickV2={({ item }) => {
            onChange({
              target: { name, value: item.value, type: 'text' },
            } as unknown as ChangeEvent<any>);
          }}
          handleClose={toggle}
          showSearchBar
          itemFilter={itemFilter}
          transformMenu="translateX(0px) translateY(0px)"
          matchAnchorWidth
          noMarginTop
          menuMaxHeight={316}
          hideAnchor
        />
      ),
    });
  };

  return (
    <TextField
      required
      variant="outlined"
      label="Service Name"
      value={selectedLabel}
      onClick={disabled ? undefined : openMenu}
      disabled={disabled}
      name={name}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <ArrowDropDownIcon />
          </InputAdornment>
        ),
      }}
      helperText={helperText}
    />
  );
};
